import { useState, useCallback, useEffect } from 'react';
import type { WidgetConfig } from './types';
import { getDefaultWidgets } from './defaultWidgets';
import { ensureDashboardSlots, placeNewChart, packChartsFromOrder } from './widgetDashboardLayout';
import { isInvoicingGatedWidget } from '../shared/invoicingGatedMetrics';

const STORAGE_KEY = 'reporting_dashboard_widgets';
const LAYOUT_VERSION = 5;

/** Optional overrides so non-overview dashboards (e.g. invoicing) persist separately. */
export interface WidgetDashboardOptions {
    storageKey?: string;
    layoutVersion?: number;
    getDefaults?: (isGm: boolean) => WidgetConfig[];
    /** Per-widget normalization when loading/persisting (dashboard-specific fixes). */
    sanitizeWidget?: (widget: WidgetConfig) => WidgetConfig;
    /** Drop retired widgets from a saved layout and close the gap. */
    dropWidget?: (widget: WidgetConfig) => boolean;
    /**
     * When false, drop invoice-dependent overview widgets from the grid + storage.
     * Omit / undefined = leave unchanged (e.g. session still loading, or invoicing dashboards).
     */
    invoicingEnabled?: boolean;
}

/**
 * Normalize a widget, preserving object identity when nothing changes so sibling
 * dashboard cards do not re-render on an unrelated widget save.
 */
function normalizeWidget(widget: WidgetConfig, sanitizeWidget?: (widget: WidgetConfig) => WidgetConfig): WidgetConfig {
    const needsLayout = widget.layout === 'horizontal';
    const needsBars = widget.showComparisonBars === undefined;
    const needsEvs = widget.excludeEvs === undefined;

    if (!needsLayout && !needsBars && !needsEvs && !sanitizeWidget) {
        return widget;
    }

    const normalized: WidgetConfig = (needsLayout || needsBars || needsEvs)
        ? {
            ...widget,
            ...(needsLayout ? { layout: 'vertical' as const } : {}),
            ...(needsBars ? { showComparisonBars: false } : {}),
            ...(needsEvs ? { excludeEvs: false } : {}),
        }
        : widget;

    return sanitizeWidget ? sanitizeWidget(normalized) : normalized;
}

function normalizeWidgets(widgets: WidgetConfig[], sanitizeWidget?: (widget: WidgetConfig) => WidgetConfig): WidgetConfig[] {
    let changed = false;
    const next = widgets.map((w) => {
        const n = normalizeWidget(w, sanitizeWidget);
        if (n !== w) changed = true;
        return n;
    });
    const slotted = ensureDashboardSlots(changed ? next : widgets);
    return slotted === widgets ? widgets : slotted;
}

/** `suppressLocationDomain` is true for single-location GMs (not multi-location GMs). */
function sanitizeWidgetsForRole(widgets: WidgetConfig[], suppressLocationDomain: boolean): WidgetConfig[] {
    if (!suppressLocationDomain) return widgets;
    return widgets.filter(widget => widget.domain !== 'location');
}

function loadWidgets(
    isGm: boolean,
    storageKey: string,
    layoutVersion: number,
    getDefaults: (isGm: boolean) => WidgetConfig[],
    sanitizeWidget?: (widget: WidgetConfig) => WidgetConfig,
    dropWidget?: (widget: WidgetConfig) => boolean,
): WidgetConfig[] {
    const fallbackDefaults = getDefaults(isGm);
    try {
        const ver = localStorage.getItem(storageKey + '_v');
        if (ver && Number(ver) >= layoutVersion) {
            const raw = localStorage.getItem(storageKey);
            if (raw) {
                const parsed = JSON.parse(raw) as WidgetConfig[];
                const normalized = normalizeWidgets(parsed, sanitizeWidget);
                const sanitized = sanitizeWidgetsForRole(normalized, isGm);
                const kept = dropWidget && sanitized.some(dropWidget)
                    ? removeAndRepack(sanitized, dropWidget)
                    : sanitized;
                const changedForRole = sanitized.length !== normalized.length;
                const changedForDrop = kept.length !== sanitized.length;
                if (
                    changedForDrop
                    || changedForRole
                    || normalized.some((w, i) => JSON.stringify(w) !== JSON.stringify(parsed[i]))
                ) {
                    try {
                        localStorage.setItem(storageKey, JSON.stringify(kept));
                    } catch { /* ignore */ }
                }
                return kept;
            }
        }
    } catch { /* ignore */ }
    try {
        localStorage.setItem(storageKey + '_v', String(layoutVersion));
        localStorage.setItem(storageKey, JSON.stringify(fallbackDefaults));
    } catch { /* ignore */ }
    return normalizeWidgets(fallbackDefaults, sanitizeWidget);
}

function saveWidgets(widgets: WidgetConfig[], storageKey: string) {
    try {
        localStorage.setItem(storageKey, JSON.stringify(widgets));
    } catch { /* ignore */ }
}

/** Drop matching widgets then pack charts top-left so empty rows/cols don't linger. */
function removeAndRepack(
    widgets: WidgetConfig[],
    shouldRemove: (widget: WidgetConfig) => boolean,
): WidgetConfig[] {
    if (!widgets.some(shouldRemove)) return widgets;
    const kept = widgets.filter((w) => !shouldRemove(w));
    const kpis = kept.filter((w) => w.widgetType === 'keyMetric');
    const charts = kept.filter((w) => w.widgetType !== 'keyMetric');
    return [...kpis, ...packChartsFromOrder(charts)];
}

export function useWidgetDashboard(isGm: boolean, options?: WidgetDashboardOptions) {
    const storageKey = options?.storageKey ?? STORAGE_KEY;
    const layoutVersion = options?.layoutVersion ?? LAYOUT_VERSION;
    const getDefaults = options?.getDefaults ?? getDefaultWidgets;

    const sanitizeWidget = options?.sanitizeWidget;
    const dropWidget = options?.dropWidget;
    const invoicingEnabled = options?.invoicingEnabled;

    const [widgets, setWidgets] = useState<WidgetConfig[]>(() => loadWidgets(
        isGm,
        storageKey,
        layoutVersion,
        getDefaults,
        sanitizeWidget,
        dropWidget,
    ));

    const persist = useCallback((updater: WidgetConfig[] | ((current: WidgetConfig[]) => WidgetConfig[])) => {
        setWidgets((current) => {
            const next = normalizeWidgets(typeof updater === 'function'
                ? (updater as (current: WidgetConfig[]) => WidgetConfig[])(current)
                : updater, sanitizeWidget);
            if (next === current) return current;
            saveWidgets(next, storageKey);
            return next;
        });
    }, [storageKey, sanitizeWidget]);

    /** Shared delete path — filters then repacks so gaps close. */
    const removeWidgetsWhere = useCallback((shouldRemove: (widget: WidgetConfig) => boolean) => {
        persist((current) => removeAndRepack(current, shouldRemove));
    }, [persist]);

    // Non-invoicing orgs: delete gated widgets via the same remove+repack path.
    useEffect(() => {
        if (invoicingEnabled !== false) return;
        removeWidgetsWhere(isInvoicingGatedWidget);
    }, [invoicingEnabled, removeWidgetsWhere]);

    // Retired charts can still be in a layout saved before the metric was removed.
    useEffect(() => {
        if (!dropWidget) return;
        removeWidgetsWhere(dropWidget);
    }, [dropWidget, removeWidgetsWhere]);

    const addWidget = useCallback((widget: WidgetConfig) => {
        if (isGm && widget.domain === 'location') return;
        if (invoicingEnabled === false && isInvoicingGatedWidget(widget)) return;
        persist((current) => {
            const placed = widget.widgetType === 'keyMetric'
                ? widget
                : placeNewChart(current.filter((w) => w.widgetType !== 'keyMetric'), widget);
            return [...current, placed];
        });
    }, [isGm, invoicingEnabled, persist]);

    const updateWidget = useCallback((id: string, updates: Partial<WidgetConfig>) => {
        if (isGm && updates.domain === 'location') return;
        if (invoicingEnabled === false && updates.metric !== undefined && isInvoicingGatedWidget({ metric: updates.metric })) {
            return;
        }
        persist((current) => {
            let changed = false;
            const next = current.map((w) => {
                if (w.id !== id) return w;
                changed = true;
                return { ...w, ...updates };
            });
            return changed ? next : current;
        });
    }, [isGm, invoicingEnabled, persist]);

    const removeWidget = useCallback((id: string) => {
        removeWidgetsWhere((w) => w.id === id);
    }, [removeWidgetsWhere]);

    const reorderWidgets = useCallback((reordered: WidgetConfig[]) => {
        persist(reordered);
    }, [persist]);

    const resetToDefault = useCallback(() => {
        persist(getDefaults(isGm));
    }, [isGm, persist, getDefaults]);

    return { widgets, addWidget, updateWidget, removeWidget, reorderWidgets, resetToDefault };
}
